import React, { useEffect, useState } from 'react'
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Select,
  DatePicker,
  Typography,
  Upload,
} from 'antd'
import axios from 'axios'
import { toast } from 'react-toastify'
import { UploadOutlined } from '@ant-design/icons'
import { AlumniCreateAPI, GetJobCategorysMaster } from '../../AllAPI/AdminAPI'
import {
  DistrictMaster,
  StateMaster,
  TalukaMaster,
} from '../../AllAPI/StudentAPI'
import { useNavigate } from 'react-router-dom'
import { validateImageUpload } from '../../Validation/FileValidation'

const { Title } = Typography
const { Option } = Select

export default function AlumniCreation() {
  const navigate = useNavigate()

  const confirmLogout = () => {
    navigate('/alumni/login')

    sessionStorage.clear()
    localStorage.clear()
  }

  const [loading, setLoading] = useState(false)
  const [form] = Form.useForm()
  const [fileList, setFileList] = useState([])

  const alumniData = JSON.parse(sessionStorage.getItem('alumniData'))
  const alumniSignupId = alumniData?.signup_id

  useEffect(() => {
    getStates()
    getCategories()

    if (alumniData) {
      form.setFieldsValue({
        f_name: alumniData.f_name,
        m_name: alumniData.m_name,
        l_name: alumniData.l_name,
        email: alumniData.email,
      })
    }
  }, [])

  // ===== Dropdown Data =====
  const [states, setStates] = useState([])
  const [districts, setDistricts] = useState([])
  const [talukas, setTalukas] = useState([])
  const [categories, setCategories] = useState([])

  // ===== API CALLS =====
  const getStates = async () => {
    try {
      const res = await axios.get(StateMaster)
      setStates(res.data)
    } catch {
      toast.error('Failed to load states')
    }
  }

  const getDistricts = async (stateId) => {
    try {
      const res = await axios.get(`${DistrictMaster}/${stateId}`)
      setDistricts(res.data)
    } catch {
      toast.error('Failed to load districts')
    }
  }

  const getTalukas = async (stateId, districtId) => {
    try {
      const res = await axios.get(`${TalukaMaster}/${stateId}/${districtId}`)
      setTalukas(res.data)
    } catch {
      toast.error('Failed to load talukas')
    }
  }

  const getCategories = async () => {
    try {
      const res = await axios.get(GetJobCategorysMaster)
      setCategories(res.data)
    } catch {
      toast.error('Failed to load categories')
    }
  }

  // ===== INITIAL LOAD =====
  useEffect(() => {
    getStates()
    getCategories()
  }, [])

  // ===== CASCADING HANDLERS =====
  const handleStateChange = (value) => {
    form.setFieldsValue({
      district: undefined,
      taluka: undefined,
    })
    setDistricts([])
    setTalukas([])
    getDistricts(value)
  }

  const handleDistrictChange = (districtId) => {
    const stateId = form.getFieldValue('state')

    form.setFieldsValue({
      taluka: undefined,
    })

    setTalukas([])

    getTalukas(stateId, districtId)
  }

   // ===== IMAGE VALIDATION =====
    const beforeUpload = (file) => {
      const isLt2MB = file.size / 1024 / 1024 < 2
  
      if (!isLt2MB) {
        toast.error('Image must be smaller than 2MB!')
        return Upload.LIST_IGNORE
      }
  
      return false
    }

  // ===== SUBMIT =====

  const onFinish = async (values) => {
    try {
      setLoading(true)

      const formData = new FormData()

      // Append all form fields
      Object.keys(values).forEach((key) => {
        if (key === 'date_of_birth' && values[key]) {
          formData.append(key, values[key].format('YYYY-MM-DD'))
        } else {
          formData.append(key, values[key] || '')
        }
      })

      // Append extra field
      formData.append('alumini_signup_id', alumniSignupId)

      // Append image file
      if (fileList.length > 0) {
        formData.append('profile_img', fileList[0].originFileObj)
      }

      const res = await axios.post(AlumniCreateAPI, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      if (res.data === 'SAVED') {
        toast.success('Form submitted successfully')
        form.resetFields()
        setFileList([])
        setDistricts([])
        setTalukas([])
        confirmLogout()
      }

      if (res.data === 'EMAIL_ALREADY_EXIST') {
        toast.warn('Email already exists')
      }
    } catch (err) {
      console.error(err)
      toast.error('Something went wrong')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="form-container mainContainer">
      <div className="row p-0 m-0 pb-5">
        <div className="col-12 col-lg-2"></div>

        <div className="col-12 col-lg-8">
          <div className="header-row">
            <h2 className="page-title">Alumni Creation</h2>
          </div>

          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            style={{
              background: '#f2f7fd',
              padding: 20,
              borderRadius: 10,
              border: '1px solid #ddd',
            }}
          >
            {/* ===== Name ===== */}
            <Row gutter={16}>
              <Col xs={24} sm={8}>
                <Form.Item
                  label="First Name"
                  name="f_name"
                  rules={[
                    { required: true, message: 'First Name is required' },
                  ]}
                >
                  <Input size="large" readOnly />
                </Form.Item>
              </Col>

              <Col xs={24} sm={8}>
                <Form.Item label="Middle Name" name="m_name">
                  <Input size="large" readOnly />
                </Form.Item>
              </Col>

              <Col xs={24} sm={8}>
                <Form.Item
                  label="Last Name"
                  name="l_name"
                  rules={[{ required: true, message: 'Last Name is required' }]}
                >
                  <Input size="large" readOnly />
                </Form.Item>
              </Col>
            </Row>

            {/* ===== Qualification ===== */}
            <Row gutter={16}>
              <Col xs={24} sm={8}>
                <Form.Item label="Upload Job Image" required>
                  <Upload
  beforeUpload={validateImageUpload}
                    accept="image/*"
                    beforeUpload={beforeUpload}
                    fileList={fileList}
                    onChange={({ fileList }) => setFileList(fileList)}
                    listType="picture"
                    maxCount={1}
                    style={{ width: '100%' }}
                  >
                    <Button
                      icon={<UploadOutlined />}
                      size="large"
                      style={{ width: '100%' }}
                    >
                      Upload
                    </Button>
                  </Upload>
                </Form.Item>
              </Col>

              <Col xs={24} sm={8}>
                <Form.Item
                  label="Qualification"
                  name="qualification"
                  rules={[
                    { required: true, message: 'Qualification is required' },
                  ]}
                >
                  <Input size="large" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={8}>
                <Form.Item
                  label="Professional Category"
                  name="professional_category"
                  rules={[
                    {
                      required: true,
                      message: 'Professional Category is required',
                    },
                  ]}
                >
                  <Select size="large" placeholder="Select Category">
                    {categories.map((item) => (
                      <Option key={item.id} value={item.id}>
                        {item.category_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {/* ===== Address ===== */}
            <Form.Item
              label="Current Address"
              name="current_address"
              rules={[{ required: true, message: 'Address is required' }]}
            >
              <Input.TextArea rows={2} />
            </Form.Item>

            <Row gutter={16}>
              <Col xs={24} sm={6}>
                <Form.Item
                  label="Pincode"
                  name="pincode"
                  rules={[
                    { required: true, message: 'Pincode is required' },
                    {
                      pattern: /^[0-9]{6}$/,
                      message: 'Pincode must be exactly 6 digits',
                    },
                  ]}
                >
                  <Input
                    size="large"
                    maxLength={6}
                    inputMode="numeric"
                    onChange={(e) => {
                      // allow only numbers
                      const value = e.target.value.replace(/\D/g, '')
                      e.target.value = value
                    }}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={6}>
                <Form.Item
                  label="State"
                  name="state"
                  rules={[{ required: true, message: 'State is required' }]}
                >
                  <Select
                    size="large"
                    placeholder="Select State"
                    onChange={handleStateChange}
                  >
                    {states.map((s) => (
                      <Option key={s.id} value={s.id}>
                        {s.stateenname}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={6}>
                <Form.Item
                  label="District"
                  name="district"
                  rules={[{ required: true, message: 'District is required' }]}
                >
                  <Select
                    size="large"
                    placeholder="Select District"
                    onChange={handleDistrictChange}
                    disabled={!districts.length}
                  >
                    {districts.map((d) => (
                      <Option key={d.id} value={d.id}>
                        {d.districtenname}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={6}>
                <Form.Item label="Taluka" name="taluka">
                  <Select
                    size="large"
                    placeholder="Select Taluka"
                    disabled={!talukas.length}
                  >
                    {talukas.map((t) => (
                      <Option key={t.id} value={t.id}>
                        {t.talukaenname}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {/* ===== Personal Info ===== */}
            <Row gutter={16}>
              <Col xs={24} sm={8}>
                <Form.Item
                  label="Date of Birth"
                  name="date_of_birth"
                  rules={[{ required: true, message: 'DOB is required' }]}
                >
                  <DatePicker size="large" style={{ width: '100%' }} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={8}>
                <Form.Item label="Blood Group" name="blood_group">
                  <Select size="large">
                    <Select.Option value="A+">A+</Select.Option>
                    <Select.Option value="A-">A-</Select.Option>
                    <Select.Option value="B+">B+</Select.Option>
                    <Select.Option value="B-">B-</Select.Option>
                    <Select.Option value="O+">O+</Select.Option>
                    <Select.Option value="O-">O-</Select.Option>
                    <Select.Option value="AB+">AB+</Select.Option>
                    <Select.Option value="AB-">AB-</Select.Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={6}>
                <Form.Item
                  label="Mobile No"
                  name="mobile"
                  rules={[
                    { required: true, message: 'Mobile no is required' },
                    {
                      pattern: /^[0-9]{10}$/,
                      message: 'Mobile no must be exactly 10 digits',
                    },
                  ]}
                >
                  <Input
                    size="large"
                    maxLength={10}
                    inputMode="numeric"
                    onChange={(e) => {
                      // allow only numbers
                      const value = e.target.value.replace(/\D/g, '')
                      e.target.value = value
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* ===== Contact ===== */}
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item label="Email" name="email">
                  <Input size="large" readOnly />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  label="Business / Service Name"
                  name="business_service_name"
                >
                  <Input size="large" />
                </Form.Item>
              </Col>
            </Row>

            {/* ===== Boarding Info ===== */}
            <Row gutter={16}>
              <Col xs={24} md={12} lg={5}>
                <Form.Item
                  label="Year Of Boarding From"
                  name="from_year_of_boarding"
                  rules={[
                    { required: true, message: 'Year is required' },
                    {
                      pattern: /^[0-9]{4}$/,
                      message: 'Year must be exactly 4 digits',
                    },
                  ]}
                >
                  <Input
                    size="large"
                    maxLength={4}
                    inputMode="numeric"
                    onChange={(e) => {
                      // allow only numbers
                      const value = e.target.value.replace(/\D/g, '')
                      e.target.value = value
                    }}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12} lg={5}>
                <Form.Item
                  label="Year Of Boarding To"
                  name="to_year_of_boarding"
                  rules={[
                    { required: true, message: 'Year is required' },
                    {
                      pattern: /^[0-9]{4}$/,
                      message: 'Year must be exactly 4 digits',
                    },
                  ]}
                >
                  <Input
                    size="large"
                    maxLength={4}
                    inputMode="numeric"
                    onChange={(e) => {
                      // allow only numbers
                      const value = e.target.value.replace(/\D/g, '')
                      e.target.value = value
                    }}
                  />
                </Form.Item>
              </Col>

              {/* ===== Reference ===== */}

              <Col xs={24} md={12} lg={9}>
                <Form.Item label="Reference Name" name="reference_fname">
                  <Input size="large" />
                </Form.Item>
              </Col>

              <Col xs={24} md={12} lg={5}>
                <Form.Item
                  label="Reference Contact"
                  name="reference_other_contact_details"
                  rules={[
                    {
                      required: true,
                      message: 'Reference Contact is required',
                    },
                    {
                      pattern: /^[0-9]{10}$/,
                      message: 'Contact must be exactly 10 digits',
                    },
                  ]}
                >
                  <Input
                    size="large"
                    maxLength={10}
                    inputMode="numeric"
                    onChange={(e) => {
                      // allow only numbers
                      const value = e.target.value.replace(/\D/g, '')
                      e.target.value = value
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item label="Achievements" name="achievements">
                  <Input.TextArea rows={2} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Suggestion" name="suggestion_comment">
                  <Input.TextArea rows={2} />
                </Form.Item>
              </Col>
            </Row>

            {/* ===== Submit ===== */}
            <Row justify="center">
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                size="large"
              >
                Submit
              </Button>
            </Row>
          </Form>
        </div>

        <div className="col-12 col-lg-2"></div>
      </div>
    </div>
  )
}
